/**
 * Spaces API Endpoints
 * API client for workspace management (multi-workspace support)
 */

import { http } from '@/lib/http'
import type { WorkspacePlanTier } from '@/types/session'

export interface SpaceLimits {
  currentCount: number
  maxAllowed: number
  canCreate: boolean
  planTier: WorkspacePlanTier
}

export interface Space {
  id: string
  name: string
  ownerId: string
  status: 'active' | 'suspended'
  createdAt: number
  /**
   * Set by the backend when the chosen plan requires Stripe checkout
   * (pro/team). The workspace row is created immediately (so the
   * subscription can bind to its id) but stays inactive until payment
   * completes. The frontend uses this to decide whether to start checkout.
   */
  paymentRequired?: boolean
}

/** Plan tier chosen at workspace-creation time. */
export type CreateSpacePlan = 'free' | 'pro' | 'team'

export interface CreateSpaceInput {
  name?: string
  /** Workspace archetype/type (optional; backend defaults it). */
  type?: string
  plan: CreateSpacePlan
  /** Seat count — only meaningful for the per-seat `team` plan. */
  seats?: number
}

export interface CreateSpaceError {
  error: string
  upgradeRequired?: boolean
  currentCount?: number
  maxAllowed?: number
  planTier?: string
}

/**
 * Get the user's workspace limits and current count
 */
export async function getSpaceLimits(authToken: string): Promise<SpaceLimits> {
  const response = await http.get<SpaceLimits>('/workspaces/limits', { authToken })
  return response.data
}

/**
 * Create a new workspace.
 *
 * Sends the chosen plan (and seat count for team) so the backend can decide
 * whether to provision immediately (free) or create an inactive workspace that
 * a subsequent Stripe checkout binds to (pro/team). Returns the new workspace —
 * `id` is used to target checkout, and `paymentRequired` signals that the
 * caller must start checkout before the workspace becomes usable.
 *
 * Throws HttpError with payload if a limit is reached.
 */
export async function createSpace(
  authToken: string,
  input: CreateSpaceInput
): Promise<Space> {
  const body: Record<string, unknown> = { plan: input.plan }
  if (input.name !== undefined) body.name = input.name
  if (input.type !== undefined) body.type = input.type
  if (input.seats !== undefined) body.seats = input.seats
  const response = await http.post<Space>('/workspaces', body, { authToken })
  return response.data
}

export interface SpaceMember {
  id: string
  email: string
  name?: string
  role: 'owner' | 'admin' | 'member' | 'read_only'
  joinedAt: number
}

/**
 * List members of a workspace
 */
export async function getSpaceMembers(
  authToken: string,
  spaceId: string
): Promise<SpaceMember[]> {
  const response = await http.get<{ members: SpaceMember[] }>(`/workspaces/${spaceId}/members`, { authToken })
  return response.data.members
}

/** Live seat usage for a team workspace (Teams S7 — GET /payment/seat-usage). */
export interface SeatUsage {
  memberCount: number
  pendingInvites: number
  used: number
  seatCap: number
  available: number
  workspaceType?: string
}

/**
 * Seat usage for the given (team) workspace. A single call answers both
 * "have invites been sent?" (`pendingInvites` / `memberCount`) and
 * "how many seats?" (`seatCap`/`used`/`available`) — used by team-owner onboarding.
 */
export async function getSeatUsage(
  authToken: string,
  spaceId: string
): Promise<SeatUsage> {
  const response = await http.get<SeatUsage>(
    `/payment/seat-usage?workspaceId=${encodeURIComponent(spaceId)}`,
    { authToken },
  )
  return response.data
}
